/* eslint-disable jsdoc/require-jsdoc */
import axios, { AxiosResponse } from 'axios';
import axiosInstance from '../_helpers/interceptor';
import { DEV_DOMAIN_URL, DEV_NEW_DOMAIN_URL, DEV_S3_DOMAIN_URL } from '../environment';
import {
  UserConfig,
  ParametersType,
  ConfigAttributes,
  summaryAPIProps,
  AddDeviceV2Props,
  UserConfigV2,
  RegisterUser,
  UpdateUserAttribute,
  ClientAttribute,
  AlertStatus,
  ClientThresholdSettingsAttribute,
  ParameterAttribute,
  EventAttribute,
  EventTypes
} from '../constants/telosair-interfaces';

const ENDPOINTS = {
  user: '/user/'
};

/**
 * GetAllDevices.
 * @returns Devices list.
 */
export const getAllDevices = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/device/devices`);
};

/**
 * GetAllDevices.
 * @returns Devices list.
 */
export const getAllDevicesV2 = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/device`);
};

/**
 * Delete device.
 * @param id - Device id.
 * @returns Axios response.
 */
export const deleteDevice = (id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/device/${id}`);
};

/**
 * Delete device.
 * @param id - Device id.
 * @param status - Device status.
 * @returns Axios response.
 */
export const changeDeviceStatus = (
  id: number,
  status: 'active' | 'inactive'
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/device/${id}/change-status`, {
    status
  });
};

/**
 * Edit device.
 * @param payload - Payload of the request.
 * @param id - Device id.
 * @returns Axios response.
 */
export const editDevice = (payload: any, id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/device/${id}`, payload);
};

/**
 * GetAllGroups.
 * @returns Groups.
 */
export const getAllGroups = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/groups/`);
};

/**
 * Delete group.
 * @param id - Group id.
 * @returns Axios response.
 */
export const deleteGroup = (id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/groups/${id}`);
};

/**
 * Edit group.
 * @param payload - Payload of the request.
 * @returns Axios response.
 */
export const editGroup = (payload: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_DOMAIN_URL}/groups/${payload.groupId}`, payload);
};

/**
 * Create group function.
 * @param data - Payload of the API.
 * @returns New group data.
 */
export const createGroup = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/groups/`, data);
};

/**
 * GetChartData.
 * @param data - Sensor details.
 * @returns Chart data.
 */
export const getChartData = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/get-chart-data`, data);
};

/**
 * GetOverViewChart.
 * @param data - Sensor details.
 * @returns Histogram data.
 */
export const getOverViewChart = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/get-overview-chart`, data);
};

/**
 * Get Alert Rules.
 * @returns Alerts data.
 */
export const getAlertRules = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/alerts/`);
};

/**
 * Delete Alert Rule.
 * @param id - Alert Rule id.
 * @returns Axios response.
 */
export const deleteAlertRule = (id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_DOMAIN_URL}/alerts/delete/${id}`);
};

/**
 * Get Activities.
 * @returns Alerts data.
 */
export const getActivities = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/activities`);
};

/**
 * Get Activities.
 * @param payload - Payload of the API.
 * @returns Alerts data.
 */
export const editActivities = (payload: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_DOMAIN_URL}/activities`, payload);
};

/**
 * Get Alerts by rule id.
 * @param id - ID of the alert rule.
 * @param start - Start time.
 * @param end - End time.
 * @returns Alerts data.
 */
export const getAlertsByRuleId = (
  id: number,
  start: number,
  end: number
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/alerts/chart/${id}/${start}/${end}`);
};

/**
 * CreateAlertRule.
 * @param data - Alert rule body.
 * @returns Response data.
 */
export const createAlertRule = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/alerts/`, data);
};

/**
 * Update Alert Rule.
 * @param data - Alert rule body.
 * @returns Response data.
 */
export const updateAlertRule = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_DOMAIN_URL}/alerts/${data.id}`, data);
};

/**
 * Function to call API to add a device.
 * @param data - Body.
 * @returns Response data.
 */
export const addDevice = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/device/add-device`, data);
};

/**
 * Function to call API to add a device.
 * @param data - Body.
 * @returns Response data.
 */
export const addDeviceV2 = (data: AddDeviceV2Props): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/device`, data);
};

/**
 * GetAllSensors.
 * @param data - Get all sensors body.
 * @returns Response data.
 */
export const getAllSensors = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/getsensorids`, data);
};

/**
 * GetDetailedTimeChart.
 * @param data - GetDetailedTimeChart body.
 * @returns Response data.
 */
export const getDetailedTimeChart = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/get-detailed-chart`, data);
};

/**
 * GetAnalyzeData.
 * @param data - GetAnalyzeData body.
 * @param data.sensors - Sesnsor to be analyzed.
 * @param data.timeSeriesType - Paramtere to be analyzed.
 * @param data.startDate - Start date.
 * @param data.endDate - End date.
 * @returns Response data.
 */
export const getAnalysisData = (data: {
  sensors: number[];
  timeSeriesType: ParametersType;
  startDate: number;
  endDate: number;
}): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/activities/analysis`, data);
};

export type PaypalPaymentRequest = {
  planName: string;
  /** Devices to buy the plan for; the backend charges the plan price per device. */
  serialNumbers: number[];
};

export type PaypalSubscriptionResponse = {
  id: number;
  clientId: number;
  /** Device the plan covers; null on legacy account-level rows. */
  serialNumber: number | null;
  planName: string;
  startDate: string;
  endDate: string;
  status: string;
  paypalOrderId: string;
};

/**
 * Fetches PayPal subscription details after redirect.
 * @param subscriptionId - Subscription ID.
 * @returns Subscription details.
 */
export const getPaypalSubscription = (
  subscriptionId: number
): Promise<AxiosResponse<PaypalSubscriptionResponse>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/subscription/${subscriptionId}`);
};

/**
 * Fetches every device bought in the same checkout as a subscription.
 * @param subscriptionId - Any subscription ID of the checkout.
 * @returns One subscription per device.
 */
export const getCheckoutDevices = (
  subscriptionId: number
): Promise<AxiosResponse<PaypalSubscriptionResponse[]>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/subscription/${subscriptionId}/devices`);
};

/** A device and the plan currently covering it; plan fields are null when it has none. */
export type DeviceSubscription = {
  serialNumber: number;
  deviceName: string;
  subscriptionId: number | null;
  planName: string | null;
  status: string | null;
  startDate: string | null;
  /** Renewal date. */
  endDate: string | null;
};

/**
 * Fetches the plan of every device in the current user's organisation.
 * @returns One entry per device.
 */
export const getDeviceSubscriptions = (): Promise<AxiosResponse<DeviceSubscription[]>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/subscriptions/devices`);
};

/**
 * Initiates PayPal checkout and returns the approval URL provided by the backend.
 * @param payload - PayPal payment request body.
 * @returns Approval URL for PayPal checkout.
 */
export const initiatePaypalPayment = async (payload: PaypalPaymentRequest): Promise<string> => {
  try {
    const response = await axiosInstance.post(`${DEV_DOMAIN_URL}/paypal/pay`, payload, {
      maxRedirects: 0,
      validateStatus: (status) => status === 200 || status === 302,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Handle 302 redirect response
    if (response.status === 302) {
      const { headers, data } = response;
      // Check headers first (standard redirect location)
      const headerLocation = headers.location || (headers as any).Location;
      if (headerLocation) {
        return Array.isArray(headerLocation) ? headerLocation[0] : headerLocation;
      }
      // Check response data (some APIs return redirect URL in body)
      if (data?.approvalUrl || data?.redirect || data?.location) {
        return data.approvalUrl || data.redirect || data.location;
      }
    }

    // Handle 200 OK response with JSON body
    if (response.status === 200) {
      const { data } = response;

      if (data?.approvalUrl) {
        return data.approvalUrl;
      }
    }

    throw new Error('Unable to initiate PayPal payment. Missing approval URL in response.');
  } catch (error: any) {
    // Handle axios error for redirects (status 302)
    // Axios may throw an error even with validateStatus for redirects
    if (error.response?.status === 302) {
      const { headers, data } = error.response;
      // Check headers first (standard redirect location)
      const headerLocation = headers.location || (headers as any).Location;
      if (headerLocation) {
        const url = Array.isArray(headerLocation) ? headerLocation[0] : headerLocation;
        return url;
      }
      // Check response data (some APIs return redirect URL in body)
      if (data?.approvalUrl || data?.redirect || data?.location) {
        return data.approvalUrl || data.redirect || data.location;
      }
    }

    // Re-throw if it's not a redirect handling case
    throw new Error('Unable to initiate PayPal payment. Missing approval URL in response.');
  }
};

/**
 * Calls PayPal cancel endpoint for handling user cancellation.
 * @returns Axios response containing backend cancel page (HTML/text).
 */
export const cancelPaypalPayment = (): Promise<AxiosResponse<string>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/cancel`, {
    responseType: 'text'
  });
};

/**
 * Interface for Export data API.
 */
export interface ExportDataProps extends ConfigAttributes {
  detailed: any[];
  end: number;
  startDate: number;
  timeSeriesType: ParametersType;
  type: string;
}

/**
 * GetAnalyzeData.
 * @param data - Export data API payload.
 * @returns Response data.
 */
export const exportData = (data: ExportDataProps): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/data/export`, data);
};

/**
 * Download s3 file.
 * @param fileName - File name.
 * @returns Response data.
 */
export const downloadS3File = (fileName: string): Promise<AxiosResponse<Blob>> => {
  return axiosInstance.get(`${DEV_S3_DOMAIN_URL}/downloadFile?fileName=${fileName}`, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/octet-stream'
    }
  });
};

/**
 * Upload S3 file response type.
 */
export type UploadS3FileResponse = {
  fileKey: string;
  presignedUrl: string;
};

/**
 * Upload sample CSV.
 * @param file - File to upload.
 * @returns Response data with key and presignedUrl.
 */
export const uploadS3File = (file: File): Promise<AxiosResponse<UploadS3FileResponse>> => {
  const formData = new FormData();
  formData.append('file', file);
  return axiosInstance.post(`${DEV_S3_DOMAIN_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * Fetches the logged in User's configuration data.
 * @returns - Returns user's configuration data.
 */
export const getUserData = (): Promise<AxiosResponse<UserConfig>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}${ENDPOINTS.user}`);
};

/**
 * Fetches GeoMap from the provided data.
 * @param data - Payload of the request.
 * @returns - Geomap data.
 */
export const getGeoMap = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/get-geo-map`, data);
};

/**
 * Fetches range-chart from the provided data.
 * @param data - Payload of the request.
 * @returns - Range chart.
 */
export const getRangeChart = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/get-range-chart`, data);
};

/**
 * Fetches temporal-trends from the provided data.
 * @param data - Payload of the request.
 * @returns - Temporal trends data.
 */
export const getTemporalTrends = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/analytics/get-temporal-trends`, data);
};

/**
 * Fetches list of parameters.
 * @returns - Parameters list.
 */
export const getParametersList = (): Promise<AxiosResponse<any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/parameters`);
};

/**
 * Fetches temporal-trends from the provided data.
 * @param data - User Unit Settings.
 * @returns - User setting data.
 */
export const saverUserUnitSettings = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/user/save`, data);
};

/**
 * Get All Gateways.
 * @returns All gateway list.
 */
export const getAllGatewaylist = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/gateway`);
};
/**
 * Delete the Gateway.
 * @param id - Gateway id.
 * @returns Axios response.
 */
export const deleteGateway = (id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/gateway/${id}`);
};
/**
 * Edit existing Gateway Record.
 * @param id - Gateway unqiue id for the record.
 * @param payload - Payload of the request.
 * @returns Axios response.
 */
export const editGateway = (id: number, payload: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/gateway/${id}`, payload);
};
/**
 * Add new Gateway Record.
 * @param data - Payload of the API.
 * @returns NewGatewayData.
 */
export const createGateway = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/gateway`, data);
};
/**
 * Get the AllNotifications.
 * @returns All Notifications.
 */
export const getNotification = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/facility/notification`);
};
/**
 * Add new Notification.
 * @param data - Payload of the API.
 * @returns NewNotification.
 */
export const createNotification = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/facility/notification`, data);
};
/**
 * Edit Notification.
 * @param id - Notificaion id.
 * @param payload -Payload of request.
 * @returns The editNotification.
 */
export const editNotification = (id: number, payload: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/facility/notification/${id}`, payload);
};
/**
 * Delete Notification.
 * @param id -Unique id to delete Notification.
 * @returns Axios response.
 */
export const deleteNotification = (id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/facility/notification/${id}`);
};
/**
 * Get All users for client.
 * @returns All users.
 */
export const getClientusers = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/users`);
};

/**
 * Create the Add User.
 * @param data - Requied data to create a user .
 * @returns The data of new User.
 */
export const createAddUSer = (data: any): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/user`, data);
};

type EditUserPayload = {
  id: number;
  firstName: string;
  lastName: string;
  status: string;
  clientId: number;
};

/**
 * Create the Add User.
 * @param data - Requied data to create a user .
 * @param id - User ID.
 * @returns The data of new User.
 */
export const editUser = (data: EditUserPayload, id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/user/${id}`, data);
};

/**
 * Create the Add User.
 * @param id - User ID.
 * @returns The data of new User.
 */
export const deleteUser = (id: number): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/user/${id}`);
};
/**
 * Get summary of the device.
 * @param payload - Payload of the API.
 * @returns All users.
 */
export const getDeviceSummary = (payload: summaryAPIProps): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/device/summary`, payload);
};

/**
 * Get summary of the group.
 * @param payload - Payload of the API.
 * @returns All users.
 */
export const getGroupSummary = (payload: summaryAPIProps): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/group/summary`, payload);
};

/**
 * Get summary of the group.
 * @param payload - Payload of the API.
 * @returns All users.
 */
export const getDataSummary = (payload: summaryAPIProps): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/summary`, payload);
};

/**
 * Get summary of the group.
 * @param payload - Payload of the API.
 * @param options - Optional config.
 * @param options.signal - AbortSignal to cancel the request.
 * @returns All users.
 */
export const getDeviceSummaryOverTime = (
  payload: summaryAPIProps & { devices?: number[] },
  options?: { signal?: AbortSignal }
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(
    `${DEV_NEW_DOMAIN_URL}/summaryOverTime`,
    { ...payload, devices: payload.devices || [] },
    { signal: options?.signal }
  );
};

/**
 * Get user configs.
 * @param payload - Payload object.
 * @returns User config.
 */
export const getMinAndMaxOfParameters = (
  payload: summaryAPIProps & { devices?: number[] }
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/device/range`, payload);
};

/**
 * Get summary of the group.
 * @param payload - Payload of the API.
 * @returns All users.
 */
export const getDevicesSummaryOverTime = (
  payload: summaryAPIProps & { devices?: number[] }
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/deviceSummaryOverTimeData`, payload);
};

/**
 * Fetch worldwide outdoor air-quality data (OpenAQ raw concentrations + WAQI
 * AQI index) for the Insight Agent's global-data tools. Proxied through the
 * Spring AI controller so the provider API keys stay server-side.
 * @param params - Request parameters.
 * @param params.lat - Latitude of the point of interest (optional).
 * @param params.lon - Longitude of the point of interest (optional).
 * @param params.city - City / place name; geocoded server-side when lat/lon are omitted.
 * @param params.radiusKm - Nearby-station search radius in km.
 * @param params.source - Provider: 'auto' (both), 'openaq', or 'waqi'.
 * @returns Normalized `{ query, aqi?, stations[], notes[] }` payload.
 */
export const getGlobalAirQuality = (params: {
  lat?: number;
  lon?: number;
  city?: string;
  radiusKm?: number;
  source?: 'auto' | 'openaq' | 'waqi';
}): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/ai/external/air-quality`, { params });
};

/**
 * Get old tags.
 * @returns Used tag list.
 */
export const getUsedTags = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_DOMAIN_URL}/alerts/tags`);
};

/**
 * Get user configs.
 * @returns User config.
 */
export const getUserConfigV2 = (): Promise<AxiosResponse<UserConfigV2, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/user/setting`);
};

/**
 * Get user configs.
 * @returns User config.
 */
export const getDeviceDataRate = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/device/dataRate`);
};

/**
 * Get user configs.
 * @param payload - Payload of the request.
 * @returns User config.
 */
export const addUserConfigV2 = (payload: UserConfigV2): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/user/setting`, {
    ...payload,
    config: JSON.stringify(payload.config)
  });
};

/**
 * Register user.
 * @param id - User id.
 * @param payload - Payload of the request.
 * @returns User config.
 */
export const updateUser = (
  id: string,
  payload: UpdateUserAttribute
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/user/${id}`, payload);
};
/**
 * Register user.
 * @param payload - Payload of the request.
 * @returns User config.
 */
export const registerUser = (payload: RegisterUser): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/public/user/register`, payload);
};

/**
 * Update Client Status.
 * @param payload - Payload of the request.
 * @param payload.id - Client id.
 * @param payload.status - Client status.
 * @param payload.clientName - Client name.
 * @returns User config.
 */
export const updateClientStatus = (payload: ClientAttribute): Promise<AxiosResponse<any, any>> => {
  return axios.post(`${DEV_NEW_DOMAIN_URL}/public/client/update-status`, payload);
};

type UpdateClientPayload = {
  id: number;
  clientName: string;
  industry: string;
  orgUrl: string;
  plan: string;
};

/**
 * Register user.
 * @param payload - Payload of the request.
 * @param id - CLient Id.
 * @returns User config.
 */
export const updateClient = (
  payload: UpdateClientPayload,
  id: number
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/client/${id}`, payload);
};

/**
 * Get user configs.
 * @param id - Id of the client.
 * @returns User config.
 */
export const getClientById = (id: string): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/public/client/${id}`);
};

/**
 * Get logged in client threshold settings.
 * @returns Threshold.
 */
export const getClientThresholdSettings = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/client/settings`);
};

/**
 * Update admin client.
 * @param payload - Payload of the request.
 * @param payload.id - Client id.
 * @param payload.firstName - First name of the user.
 * @param payload.lastName - Last name of the user.
 * @param payload.role - Role of the user.
 * @param payload.status - Status of the user.
 * @param payload.userName - Username of the user.
 * @param payload.clientId - Client ID associated with the user.
 * @param id - Client id.
 * @returns Updated data.
 */
export const updateAdminClient = (
  payload: {
    id: number;
    firstName: string;
    lastName: string;
    role: string;
    status: string;
    userName: string;
    clientId: number;
  },
  id: number
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/admin/update/client/${id}`, payload);
};

export const generatePublicToken = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/admin/generate-view-token`);
};

/**
 * Get logged in client threshold settings.
 * @param newConfig - Updated config.
 * @returns Threshold.
 */
export const updateClientThresholdSettings = (
  newConfig: ClientThresholdSettingsAttribute
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/client/settings`, newConfig);
};

/**
 * Get user configs.
 * @param id - Client id.
 * @returns User config.
 */
export const getCurrentLoggedInCLient = (id: number): Promise<AxiosResponse<any, any>> => {
  return axios.get(`${DEV_NEW_DOMAIN_URL}/public/client/${id}`);
};

/**
 * Fetches monthly trends for sensors.
 * @param data -Data.
 * @param data.sensors - Sesnsor to be analyzed.
 * @param data.parameter - Paramtere to be analyzed.
 * @param data.startDate - Start date.
 * @param data.endDate - End date.
 * @param data.clients - Sesnsor to be analyzed.
 * @param data.timeZone - Paramtere to be analyzed.
 * @param data.type - Start date.
 * @returns - User setting data.
 */
export const getAnlayiticsTrends = (data: {
  parameter: string;
  sensors: string;
  timeZone: string;
  startDate: number;
  endDate: number;
  clients: [string];
  type: string;
}): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/analytics/trends`, data);
};

type SearchAlert = {
  searchString?: string;
  sortId?: string;
  startDate?: number;
  endDate?: number;
  devices?: number[];
  parameters?: string[];
  statuses?: AlertStatus[];
};
/**
 * Search Alerts.
 * @param payload - Payload of the request.
 * @param payload.searchString - Search string.
 * @param payload.sortId - Sort ID.
 * @param cancelToken - Axios cancel token.
 * @returns Alerts.
 */
export const getAlertsWithSearch = (
  payload: SearchAlert,
  cancelToken?: any
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/alerts/search`, payload, {
    cancelToken
  });
};

type AlertUpdatePayload = {
  tags?: string[];
  noteTitle?: string | null;
  status?: AlertStatus;
  activity?: string;
  /** Replaces the alert's assignees (user names). */
  assignedTo?: string[];
};
/**
 * Update Alert.
 * @param payload - Payload of the API.
 * @param id - Alert ID.
 * @returns Alerts data.
 */
export const updateAlert = (
  payload: AlertUpdatePayload,
  id: string
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/alerts/${id}`, payload);
};

/**
 * Update parameter sequence.
 * @param payload - Payload of the API.
 * @returns Updated data.
 */
export const updateParameterSequence = (
  payload: ParameterAttribute[]
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/parameters/bulk/update`, payload);
};

/**
 * Update parameter sequence.
 * @param payload - Payload of the API.
 * @param id - Alert ID.
 * @returns Updated data.
 */
export const updateParameterSequenceById = (
  payload: ParameterAttribute,
  id: number
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.put(`${DEV_DOMAIN_URL}/parameters/${id}`, payload);
};

/**
 * Update parameter sequence.
 * @param payload - Payload of the API.
 * @returns Updated data.
 */
export const createEvent = (
  payload: EventAttribute
): Promise<AxiosResponse<EventAttribute, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/event`, payload);
};

/**
 * Update parameter sequence.
 * @param payload - Payload of the API.
 * @param id - Alert ID.
 * @returns Updated data.
 */
export const updateEvent = (
  payload: EventAttribute,
  id: number
): Promise<AxiosResponse<EventAttribute, any>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/event/${id}`, payload);
};

/**
 * Get events.
 * @param type - Type of event.
 * @returns Updated data.
 */
export const getEvents = (type: EventTypes): Promise<AxiosResponse<EventAttribute[], any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/event/all?eventType=${type}`);
};

/**
 * Get events.
 * @returns Updated data.
 */
export const getCurrentUser = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/me`);
};

/**
 * Get admin clients.
 * @returns Updated data.
 */
export const getAdminClients = (): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/admin/client`);
};

/**
 * Delete Alert.
 * @param id - Alert ID.
 * @returns Alerts data.
 */
export const deleteAlert = (id: string): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/alerts/${id}`);
};

export type GetScalingCalculationsProps = {
  parameter: string;
  toSensor: string;
  fromSensors: string;
  startDate: number;
  endDate: number;
};

/**
 * Get summary of the group.
 * @param payload - Payload of the API.
 * @returns All users.
 */
export const getScalingCalculations = (
  payload: GetScalingCalculationsProps
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_DOMAIN_URL}/sensor/scaling/get-calculations`, payload);
};

/**
 * Resend temporary password to user.
 * @param username - Username to resend temporary password to.
 * @returns Promise with response.
 */
export const resendTempPassword = (username: string): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(
    `${DEV_NEW_DOMAIN_URL}/resend/temp-password?username=${encodeURIComponent(username)}`
  );
};

/** A freshly minted invitation link; the token is shown once and never stored in plain form. */
export type InviteLink = { token: string; expiresAt: string; userName: string };

/** Who an invitation link is for, shown before the invitee picks a password. */
export type InviteInfo = { userName: string; firstName?: string; expiresAt: string };

/**
 * Creates a one-time invitation link for a user who hasn't set a password yet (Admin only).
 * @param userId - The invitee's user id.
 * @returns The token and its expiry.
 */
export const createInviteLink = (userId: number): Promise<AxiosResponse<InviteLink>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/user/${userId}/invite-link`);
};

/**
 * Builds the URL an Admin copies for an invite token.
 * @param token - Token from createInviteLink.
 * @returns Absolute link to the accept-invite page on this site.
 */
export const buildInviteUrl = (token: string): string =>
  `${window.location.origin}/auth/accept-invite?token=${encodeURIComponent(token)}`;

/**
 * Looks up an invitation link. Public: the invitee isn't signed in.
 * @param token - Token from the link.
 * @returns Who the invite is for.
 */
export const lookupInvite = (token: string): Promise<AxiosResponse<InviteInfo>> => {
  return axios.post(`${DEV_NEW_DOMAIN_URL}/public/invite/lookup`, { token });
};

/**
 * Accepts an invitation by choosing a password. Public.
 * @param token - Token from the link.
 * @param password - New password.
 * @param confirmPassword - Repeat of the new password.
 * @returns Empty response on success.
 */
export const acceptInvite = (
  token: string,
  password: string,
  confirmPassword: string
): Promise<AxiosResponse<void>> => {
  return axios.post(`${DEV_NEW_DOMAIN_URL}/public/invite/accept`, {
    token,
    password,
    confirmPassword
  });
};

/**
 * Ingest device data from S3 file.
 * @param s3FilePath - Path to file in S3.
 * @param deviceNumber - Device number to ingest data for.
 * @returns Promise with response.
 */
export const ingestDeviceData = (
  s3FilePath: string,
  deviceNumber: string
): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(
    `${DEV_DOMAIN_URL}/data-ingestion/ingestDeviceData?s3FilePath=${encodeURIComponent(
      s3FilePath
    )}&deviceNumber=${encodeURIComponent(deviceNumber)}`
  );
};

export const getOutdoorDeviceData = (payload: {
  endDate: number;
  devices: number[];
}): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/outdoor/devices/average`, {
    startDate: payload.endDate - 1000 * 60 * 60 * 24,
    ...payload
  });
};

export const requestAccountDeletion = (payload: {
  email: string;
  reason: string;
}): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/user/request-deletion`, payload);
};

export const createPublicToken = (payload: {
  deviceId: number;
  groupId: number;
  validTill: string;
}): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/public-tokens`, payload);
};

/**
 * Fetches before and after device metrics surrounding a time range for given devices.
 * @param payload - Request payload.
 * @param payload.devices - Array of device IDs.
 * @param payload.startTime - Start time in UNIX epoch milliseconds.
 * @param payload.endTime - End time in UNIX epoch milliseconds.
 * @param payload.timeDifference - Time difference (presumably in minutes).
 * @returns Promise with response containing beforeStartTime and afterEndTime metrics.
 */
export const getDeviceTimeRangeMetrics = (payload: {
  devices: number[];
  startTime: number;
  endTime: number;
  timeDifference: number;
}): Promise<
  AxiosResponse<{
    beforeStartTime: Record<number, any>;
    afterEndTime: Record<number, any>;
    duringTime: Record<number, any>;
  }>
> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/device/timeRange`, payload);
};

export type ExternalReportCreatePayload = {
  key: string;
  startTimeColumn: string;
  endTimeColumn: string;
  defaultDevices: string[];
  timeOffset: number;
};

export type ExternalReportUpdatePayload = {
  startTimeColumn: string;
  endTimeColumn: string;
  defaultDevices: string[];
  timeOffset: number;
};

export type ExternalReport = {
  key: string;
  startTimeColumn: string;
  endTimeColumn: string;
  defaultDevices: string[];
  timeOffset?: number;
  createdTime?: string;
  updatedAt?: string;
};

/**
 * Create external report.
 * @param payload - Payload of the request.
 * @returns Response data.
 */
export const createExternalReport = (
  payload: ExternalReportCreatePayload
): Promise<AxiosResponse<ExternalReport>> => {
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/external-reports`, payload);
};

/**
 * Get all external reports.
 * @returns All external reports.
 */
export const getAllExternalReports = (): Promise<AxiosResponse<ExternalReport[]>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/external-reports`);
};

/**
 * Get external report by key.
 * @param key - Report key.
 * @returns External report data.
 */
export const getExternalReportByKey = (key: string): Promise<AxiosResponse<ExternalReport>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/external-reports/${key}`);
};

/**
 * Update external report.
 * @param key - Report key.
 * @param payload - Payload of the request.
 * @returns Updated report data.
 */
export const updateExternalReport = (
  key: string,
  payload: ExternalReportUpdatePayload
): Promise<AxiosResponse<ExternalReport>> => {
  return axiosInstance.put(`${DEV_NEW_DOMAIN_URL}/external-reports/${key}`, payload);
};

/**
 * Delete external report.
 * @param key - Report key.
 * @returns Axios response.
 */
export const deleteExternalReport = (key: string): Promise<AxiosResponse<any, any>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/external-reports/${key}`);
};

// export const getAllClients = () => {
//   return axiosInstance.get(`${DEV_DOMAIN_URL_LOCAL}/device/get-distinct-devices`);
// };
// export const getAllFacility = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/getfacilitydetails`, data);
// };

// export const getAllZones = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/getzonedetails`, data);
// };

// export const getAllParticleData = () => {
//   return axiosInstance.get(`${DEV_DOMAIN_URL_LOCAL}/getData`);
// };

// export const getTimeSeriesChart = () => {
//   return axiosInstance.get(`${DEV_DOMAIN_URL_LOCAL}/getParticleChartData`);
// };

// export const getRangeChart = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/get-range-chart`, data);
// };

// export const getRooms = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/getrooms`, data);
// };

// export const getFloorData = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/get-floor-chart`, data);
// };

// export const getRealTimeData = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/get-real-time-data`, data);
// };

// export const exportToCSV = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/export-csv`, data);
// };

// export const exportToRawCSV = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/export-raw-csv`, data);
// };

// export const getDetailedTimeChart = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/get-detailed-chart`, data);
// };

// export const sendDataToKafka = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/send-to-kafka`, data);
// };

// export const getDNDRealTimeData = (data: any) => {
//   return axiosInstance.post(`${DEV_DOMAIN_URL_LOCAL}/dnd/get-dnd`, data);
// };

// export const getOneMinInterval = () => {
//   return axiosInstance.get(`${DEV_DOMAIN_URL_LOCAL}/dnd/get-1-interval`);
// };

// export const getTypeDetails = () => {
//   return axiosInstance.get(`${DEV_DOMAIN_URL_LOCAL}/getProps`);
// };
